import React, { useEffect, useState } from 'react'

import { useParams, useHistory } from 'react-router-dom'
import { Box, Button, Tooltip } from '@material-ui/core'
import { FormContext, useForm } from 'react-hook-form'
import { HelpCircle as HelpCircleIcon } from 'react-feather'

import {
  Container,
  ContentHeader,
  DriveTour,
  LoadingFeedback,
  Page,
} from 'components'
import {
  PrivacyByDesignForm,
  PrivacyByDesignQuestionReply,
  PrivacyByDesignReviewActions,
  PrivacyByDesignReviewPanel,
  PrivacyByDesignSendToReviewButton,
} from './components'

import useFetch from 'hooks/useFetch'
import usePrivacyByDesignAnswers from 'hooks/usePrivacyByDesignAnswers'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import constants from 'constants/index'
import { routes } from 'Routes'
import helpers from 'helpers'
import schema from './components/PrivacyByDesignForm/schema'
import {
  buildDefaultValues,
  buildGeneralInfoPayload,
} from './components/PrivacyByDesignForm/formMappers'

const PrivacyByDesignEdit = () => {
  const [openTour, setOpenTour] = useState(false)

  const history = useHistory()
  const params = useParams()
  const snackbar = useSnackbar()

  const privacyByDesignId = params.privacyByDesignId

  const {
    EDIT_PRIVACY_BY_DESIGN_FORM_DRIVER_STEPS_OBJECT,
    PRIVACY_BY_DESIGN_FORM_DRIVER_STEP_0,
    PRIVACY_BY_DESIGN_FORM_DRIVER_STEP_1,
    PRIVACY_BY_DESIGN_FORM_DRIVER_STEP_3,
  } = constants.privacyByDesign

  const {
    response,
    isLoading: isLoadingPrivacyByDesign,
    refresh,
  } = useFetch(service.dponet.privacyByDesign.get, {
    privacyByDesignId,
  })

  const privacyByDesign = response?.data?.privacyByDesign

  const {
    evidencesByAnswerId,
    loadPrivacyByDesign,
    questions,
    removeEvidence,
    reopenAnswer,
    saveAnswer,
    saveStateByQuestionId,
  } = usePrivacyByDesignAnswers({
    privacyByDesignId,
    service: service.dponet.privacyByDesign,
  })

  const formMethods = useForm({
    validationSchema: schema({ mode: 'edit' }),
    defaultValues: buildDefaultValues(privacyByDesign, { mode: 'edit' }),
  })

  const { reset } = formMethods

  const redirectToMain = () => history.push(routes.privacyByDesign.all)

  // Salva apenas os dados gerais do projeto (nome/descrição/responsável) pelo
  // botão do card. Não envia respostas nem reseta o formulário.
  const handleSaveProjectFields = async (values) => {
    try {
      await service.dponet.privacyByDesign.update({
        privacyByDesignId,
        ...buildGeneralInfoPayload(values),
      })

      snackbar.open({
        message: 'Informações gerais salvas com sucesso!',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    }
  }

  const handleOpenTour = () => {
    const where = 'Privacy By Design'
    const title = 'Tour'

    helpers.mixpanel.track(where, title, {
      action: 'tour-privacy-by-design',
    })
    setOpenTour(true)
  }

  // Este formulário cuida apenas das informações gerais; cada questão tem o
  // próprio formulário, alimentado pelo hook das respostas.
  useEffect(() => {
    if (privacyByDesign) {
      reset(buildDefaultValues(privacyByDesign, { mode: 'edit' }))
      loadPrivacyByDesign(privacyByDesign)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privacyByDesign, reset])

  return (
    <Page title="Edição de Projeto - Privacy By Design">
      <Container maxWidth={false}>
        <LoadingFeedback open={isLoadingPrivacyByDesign} />
        <ContentHeader title="Privacy By Design">
          <Tooltip title="Iniciar o tour guiado">
            <Button
              id={PRIVACY_BY_DESIGN_FORM_DRIVER_STEP_0}
              variant="contained"
              color="primary"
              startIcon={<HelpCircleIcon size={20} />}
              onClick={handleOpenTour}
              fullWidth
            >
              Tutorial
            </Button>
          </Tooltip>
        </ContentHeader>
        <FormContext {...formMethods}>
          {privacyByDesign && (
            <PrivacyByDesignForm
              mode="edit"
              privacyByDesign={privacyByDesign}
              usersService={service.dponet.privacyByDesign.getUsers}
              onSubmit={handleSaveProjectFields}
              cardId={PRIVACY_BY_DESIGN_FORM_DRIVER_STEP_1}
            />
          )}
          <PrivacyByDesignReviewPanel
            privacyByDesign={privacyByDesign}
            actions={
              <PrivacyByDesignReviewActions
                privacyByDesign={privacyByDesign}
                service={service.dponet.privacyByDesign}
                refresh={refresh}
                // Reabrir devolve o projeto para pendente e a edição é onde ele
                // continua: recarrega no lugar em vez de voltar para a listagem.
                onReopen={refresh}
              />
            }
          />
          <PrivacyByDesignQuestionReply
            questions={questions}
            isLoading={isLoadingPrivacyByDesign}
            evidencesByAnswerId={evidencesByAnswerId}
            onRemoveEvidence={removeEvidence}
            onSaveAnswer={saveAnswer}
            onReopenAnswer={reopenAnswer}
            saveStateByQuestionId={saveStateByQuestionId}
            hasReviewerOpinion={!!privacyByDesign?.latestReviewerOpinion}
          />
        </FormContext>
        <Box ml={2} mt={2} gridGap={5} display="flex" justifyContent="flex-end">
          <Box>
            <Button type="button" variant="outlined" onClick={redirectToMain}>
              Voltar
            </Button>
          </Box>
          <PrivacyByDesignSendToReviewButton
            privacyByDesignId={privacyByDesignId}
            service={service.dponet.privacyByDesign}
            questions={questions}
            onSuccess={redirectToMain}
            id={PRIVACY_BY_DESIGN_FORM_DRIVER_STEP_3}
          />
        </Box>
      </Container>
      <DriveTour
        stepsMatrix={EDIT_PRIVACY_BY_DESIGN_FORM_DRIVER_STEPS_OBJECT}
        open={openTour}
        setOpen={setOpenTour}
      />
    </Page>
  )
}

export default PrivacyByDesignEdit
